/**
 * Shared components for AI Action pages (Call, Email, Text, Appointment)
 * These components reduce code duplication across the ZOEY AI action pages
 */

import { ArrowLeft, User, Clock, Building, Phone, Mail, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Back button with lead name - consistent across all AI action pages
 */
export function ActionBackButton({ leadId, leadName, className = "" }) {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate(`/admin/leads/${leadId}`)}
      className={`flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm ${className}`}
      data-testid="back-button"
    >
      <ArrowLeft className="w-4 h-4" />
      <span>Back to {leadName}'s Profile</span>
    </button>
  );
}

/**
 * Lead info card header - shows avatar, name, contact info, and score
 */
export function LeadInfoCard({ lead, accentColor = "green" }) {
  const colorMap = {
    green: { bg: "bg-green-500", badge: "bg-green-500/20 text-green-400 border-green-500/30" },
    blue: { bg: "bg-blue-500", badge: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    purple: { bg: "bg-purple-500", badge: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
    amber: { bg: "bg-amber-500", badge: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  };
  
  const colors = colorMap[accentColor] || colorMap.green;
  const phoneNumber = lead?.mobile_phone || lead?.company_phone;

  return (
    <div className="space-y-2">
      <h2 className="text-xl font-bold text-white">{lead?.first_name} {lead?.last_name}</h2>
      <p className="text-zinc-400 text-sm">
        {phoneNumber || lead?.email || "No contact"} 
        {lead?.job_title && ` · ${lead.job_title}`}
      </p>
      <div className="flex items-center gap-2">
        {lead?.ai_score && (
          <span className={`px-2.5 py-1 text-xs font-medium rounded-md border ${colors.badge}`}>
            Score {lead.ai_score}
          </span>
        )}
        {lead?.status === "Qualified" && (
          <span className={`px-2.5 py-1 text-xs font-medium rounded-md border ${colors.badge}`}>
            Qualified
          </span>
        )}
        {lead?.company_name && (
          <span className="px-2.5 py-1 bg-zinc-800 text-zinc-400 text-xs font-medium rounded-md border border-zinc-700">
            {lead.company_name}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Page header with icon and title - consistent across AI action pages
 */
export function ActionPageHeader({ icon: Icon, title, subtitle, gradientFrom, gradientTo }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl bg-gradient-to-br from-${gradientFrom} to-${gradientTo}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h1 className="text-lg font-bold text-white">{title}</h1>
        <p className="text-xs text-zinc-500">{subtitle}</p>
      </div>
    </div>
  );
}

/**
 * Statistics grid - 2x2 or 4x1 layout for action page stats
 */
export function ActionStatsGrid({ stats, columns = 2 }) {
  return (
    <div className={`grid grid-cols-${columns} gap-3`}>
      {stats.map((stat, idx) => (
        <div key={idx} className="bg-zinc-900/80 rounded-xl p-4 text-center border border-zinc-800">
          <p className={`text-2xl font-bold ${stat.color || "text-white"}`}>{stat.value}</p>
          <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wide">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Tips list component - displays helpful tips with chevron icons
 */
export function ActionTipsList({ tips, accentColor = "green" }) {
  const colorMap = {
    green: "text-green-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    amber: "text-amber-400",
  };
  const chevronColor = colorMap[accentColor] || colorMap.green;

  return (
    <div className="space-y-3">
      {tips.map((tip, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <span className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${chevronColor}`}>›</span>
          <p className="text-xs text-zinc-400">{tip}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * History item card - for displaying past actions (calls, emails, texts)
 */
export function HistoryItemCard({ 
  date, 
  status, 
  statusColor = "green",
  title, 
  subtitle, 
  onClick,
  testId 
}) {
  const statusColorMap = {
    green: "bg-green-500/20 text-green-400",
    blue: "bg-blue-500/20 text-blue-400",
    amber: "bg-amber-500/20 text-amber-400",
    red: "bg-red-500/20 text-red-400",
    gray: "bg-zinc-700/50 text-zinc-400",
  };

  return (
    <div 
      onClick={onClick}
      className={`bg-zinc-900/60 rounded-xl p-4 border border-zinc-800/50 ${onClick ? "cursor-pointer hover:border-zinc-700" : ""} transition-all`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-500">{date}</span>
        {status && (
          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColorMap[statusColor]}`}>
            {status}
          </span>
        )}
      </div>
      {title && <p className="text-sm font-medium text-white">{title}</p>}
      {subtitle && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{subtitle}</p>}
    </div>
  );
}

/**
 * Section header with optional action button
 */
export function SectionHeader({ title, action, actionLabel, actionIcon: ActionIcon }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest">{title}</p>
      {action && (
        <button 
          onClick={action}
          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          {ActionIcon && <ActionIcon className="w-3 h-3" />}
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Tab navigation component
 */
export function ActionTabs({ tabs, activeTab, onTabChange, accentColor = "green" }) {
  const colorMap = {
    green: "text-green-400 border-green-400",
    blue: "text-blue-400 border-blue-400",
    purple: "text-purple-400 border-purple-400",
    amber: "text-amber-400 border-amber-400",
  };
  const activeStyle = colorMap[accentColor] || colorMap.green;

  return (
    <div className="flex gap-6 border-b border-zinc-800 mb-4">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? `${activeStyle} border-b-2`
              : "text-zinc-500 hover:text-white"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Loading spinner component
 */
export function ActionLoader({ color = "green" }) {
  const colorMap = {
    green: "text-green-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    amber: "text-amber-400",
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className={`w-8 h-8 animate-spin ${colorMap[color]}`}>
        <Sparkles className="w-full h-full" />
      </div>
    </div>
  );
}

/**
 * Empty state component
 */
export function EmptyState({ icon: Icon, message, accentColor = "green" }) {
  return (
    <div className="text-center py-8 bg-zinc-900/30 rounded-xl border border-zinc-800/30">
      <Icon className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
      <p className="text-xs text-zinc-600">{message}</p>
    </div>
  );
}
